import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {NodeProps} from 'reactflow';

import {normalizeNameForUniqueness} from 'procedures/design/flowchartManagementUtils';
import {ProcedureAccessLevel} from 'procedures/design/validation/determineProcedureAccessLevel';
import {validateMethodName} from 'procedures/design/validation/methodValidationUtils';
import {ProcedurePageContext} from 'procedures/ProcedurePageContext';
import {multiLineEllipsisText} from 'styles/Utilities';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY, GRAY_DARK, GRAY_DARKER, GRAY_LIGHT, NEAR_BLACK} from 'ui-library/Colors';
import FieldError from 'ui-library/FieldError';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useHoverState from 'ui-library/hooks/useHoverState';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import removeNulls from 'util/removeNulls';
import {ElementMouseEvent} from 'util/Types';

import {
  NodeLocationType,
  ProcedureEndsMethodNodeAddNodeButton,
} from './buttons/ProcedureEndsMethodNodeAddNodeButton';
import ProcedureValidationErrorInfoButton from './buttons/ProcedureValidationErrorInfoButton';
import DoubleClickEditableTextField, {EmptyStringCompletionPolicy} from './DoubleClickEditableTextField';
import {createHandleIdForMethod} from './handleIdentifierUtils';
import {MAX_METHOD_NAME_LENGTH} from './ProcedureDesignConstants';
import {ProcedureEditorContext} from './ProcedureEditorContext';
import {ProcedureNodeHandle} from './ProcedureHandles';
import {
  getMethodNamePlaceholder,
  makeNodeWrapperWithOpacityClass,
  makePlaceholderNameClass,
  methodBodyClass,
  NODE_NAME_BOX_PADDING,
  nodeNameBoxClass,
  nodeNameTextEllipsesClass,
} from './procedureMethodNodeCommonConsts';
import {
  ProcedureEquipmentMethodSection,
  ProcedureMaterialsMethodSection,
  ProcedureParametersMethodSection,
} from './procedureMethodNodeRendererSections';
import {
  FilteredProcedureMethodSection,
  getSectionOptionUIString,
} from './ProcedureMethodSectionFilterDropdown';
import ProcedureMethodSettingsDropdown from './ProcedureMethodSettingsDropdown';
import {MethodCanvasNodeData} from './ProcedureReactFlowCanvasTypes';
import {useGridLayoutNodeDOMRefCallback} from './useConvertedGridLayoutGraph';
import {ProcedureValidationContext} from './validation/ProcedureValidationContext';
import {
  ProcedureValidationErrorType,
  ProcedureValidationObjectType,
} from './validation/ProcedureValidationTypes';
import {findRelevantProcedureValidationErrors} from './validation/procedureValidationUtils';

const ALLOWABLE_ERRORS_DURING_NAME_EDITS = [
  ProcedureValidationErrorType.INVALID_NAME,
  ProcedureValidationErrorType.NON_UNIQUE_NAME,
];

/**
 * Reactflow relies on 'nodrag' class to prevent elements from dragging a node.
 * Use for any elements within the method that should not be draggable.
 *
 * Ref: https://reactflow.dev/docs/api/nodes/custom-nodes/#prevent-dragging--selecting
 */
function noDragClass(otherClasses?: string): string {
  return classNames('nodrag', otherClasses);
}

function useMethodNameOverflowCheck(ref: React.RefObject<HTMLDivElement>): boolean {
  const [isOverflow, setIsOverflow] = useState(false);

  useEffect(() => {
    const checkOverflow = (): void => {
      if (ref.current) {
        setIsOverflow(ref.current.scrollHeight > ref.current.offsetHeight + NODE_NAME_BOX_PADDING);
      }
    };
    checkOverflow();
  }, [ref]);

  return isOverflow;
}

export function MethodNameViewer(props: {
  methodCid: string;
  name: string;
  isHoverable: boolean;
}): JSX.Element {
  const [onHover, hoverProps] = useHoverState();
  const methodNameRef = useRef<HTMLDivElement>(null);
  const isMethodNameOverflow = useMethodNameOverflowCheck(methodNameRef);
  const methodNameContent = (
    <div {...getTestProps({component: 'MethodNameViewer', element: 'root'})}>{props.name}</div>
  );

  return (
    <div id={`${props.methodCid}-viewer`} className={noDragClass(css(styles.nameAndValidationBox))}>
      <ProcedureValidationErrorInfoButton
        primaryObject={{id: props.methodCid, objectType: ProcedureValidationObjectType.METHOD}}
      />
      <div
        ref={methodNameRef}
        className={classNames(
          nodeNameTextEllipsesClass,
          makePlaceholderNameClass(props.name),
          css(props.isHoverable && onHover && styles.onHoverName)
        )}
        {...hoverProps}
      >
        <Tooltip
          placement={FloatingPlacement.TOP}
          text={methodNameContent}
          isDisabled={!isMethodNameOverflow}
        >
          {methodNameContent}
        </Tooltip>
      </div>
    </div>
  );
}

export function ProcedureMethodEditorViewButton(props: {
  methodCid: string;
  onViewMethodEditor: (nodeId: string) => void;
}): JSX.Element {
  return (
    <BenchlingIcon
      className={css(styles.viewMethodEditorButton)}
      icon="eye"
      onClick={() => {
        props.onViewMethodEditor(props.methodCid);
      }}
    />
  );
}

export default function ProcedureMethodNodeRenderer(props: NodeProps<MethodCanvasNodeData>): JSX.Element {
  const methodCid = props.data.cid;

  const {accessLevel, setShouldPauseAutosave, procedureMetadata} = ProcedurePageContext.useContext();
  const procedureType = procedureMetadata.procedureType;
  const hasFullAccess = accessLevel === ProcedureAccessLevel.FULL_ACCESS;

  const [isHovered, hoverProps] = useHoverState();
  const {
    handlesInUse,
    methodNamesInUse,
    editingNameMethodCid,
    setEditingNameMethodCid,
    methodCidBeingDragged,
    selectedMethodSectionFilter,
    isProcedureReadOnly,
  } = ProcedureEditorContext.useContext();
  const parameterUIString = useMemo(
    () => getSectionOptionUIString(FilteredProcedureMethodSection.PARAMETERS),
    []
  );
  const equipmentUIString = useMemo(
    () => getSectionOptionUIString(FilteredProcedureMethodSection.EQUIPMENT),
    []
  );
  // While this node name is being edited, block the user from dragging. Otherwise, drags inside
  // the text editor will begin a node-drag, rather than a text-select
  const wrapperClass = editingNameMethodCid === methodCid ? noDragClass() : '';

  const [editingNameError, setEditingNameError] = useState<string | null>(null);
  const isEditingName = editingNameMethodCid === methodCid;
  const setIsEditingName = useCallback(
    (newIsEditingName: boolean): void => {
      setEditingNameMethodCid(newIsEditingName ? methodCid : null);
      if (!newIsEditingName) {
        setEditingNameError(null);
      }
    },
    [methodCid, setEditingNameMethodCid]
  );

  useEffect(() => {
    setShouldPauseAutosave(isEditingName);
  }, [isEditingName, setShouldPauseAutosave]);

  const {procedureValidationErrors} = ProcedureValidationContext.useContext();
  const methodValidationErrors = findRelevantProcedureValidationErrors(procedureValidationErrors, {
    id: methodCid,
    objectType: ProcedureValidationObjectType.METHOD,
  });

  const hasValidationErrors =
    methodValidationErrors.filter((error) => {
      if (editingNameMethodCid) {
        // Don't show method as invalid if the user is actively editing the
        // name and the only errors are related to the method's name.
        return !(
          error.primaryObject.id === methodCid && ALLOWABLE_ERRORS_DURING_NAME_EDITS.includes(error.type)
        );
      } else {
        return true;
      }
    }).length !== 0;

  const allMethodNamesExcludingSelectedMethod = removeNulls(
    Object.values({...methodNamesInUse, [methodCid]: null})
  );

  const gridDomRefCallback = useGridLayoutNodeDOMRefCallback();
  const refCallback = useCallback(
    (domNode) => {
      gridDomRefCallback(methodCid, domNode);
    },
    [methodCid, gridDomRefCallback]
  );
  const isLeafNode = !handlesInUse.includes(createHandleIdForMethod(methodCid, 'source'));
  const isRootNode = !handlesInUse.includes(createHandleIdForMethod(methodCid, 'target'));
  const shouldRenderMethodSection = (sectionName: FilteredProcedureMethodSection): boolean =>
    selectedMethodSectionFilter.includes(sectionName);

  const getDefaultNameWhenEmpty = (): string =>
    normalizeNameForUniqueness(
      '',
      allMethodNamesExcludingSelectedMethod,
      getMethodNamePlaceholder(procedureType)
    );

  function handleDoubleClick(e: ElementMouseEvent): void {
    // If the user is editing a method name in the flowchart view, or doesn't have access to view the method details,
    // don't act on a double-click here
    if (editingNameMethodCid == null && hasFullAccess) {
      e.stopPropagation();
      props.data.onEditMethod(methodCid);
    }
  }

  return (
    <div
      onDoubleClick={handleDoubleClick}
      className={wrapperClass}
      {...getTestProps({
        component: 'ProcedureMethodNodeRenderer',
        element: 'root',
        key: props.data.name,
      })}
    >
      {isRootNode && !isProcedureReadOnly && hasFullAccess && (
        <ProcedureEndsMethodNodeAddNodeButton
          methodCid={methodCid}
          onClick={props.data.onAddMethod}
          nodeLocationType={NodeLocationType.ROOT}
        />
      )}
      {isLeafNode && !isProcedureReadOnly && hasFullAccess && (
        <ProcedureEndsMethodNodeAddNodeButton
          methodCid={methodCid}
          onClick={props.data.onAddMethod}
          nodeLocationType={NodeLocationType.LEAF}
        />
      )}
      <div
        className={makeNodeWrapperWithOpacityClass(
          props.selected && !isProcedureReadOnly,
          methodCidBeingDragged
        )}
        ref={refCallback}
      >
        <div className={nodeNameBoxClass(hasValidationErrors, Boolean(editingNameError))}>
          {!isProcedureReadOnly && hasFullAccess ? (
            <DoubleClickEditableTextField
              onComplete={(changedValue: string) => {
                props.data.onChangeMethodName(methodCid, changedValue.trim());
              }}
              viewer={<MethodNameViewer methodCid={methodCid} name={props.data.name} isHoverable={true} />}
              isEditing={isEditingName}
              setIsEditing={setIsEditingName}
              value={props.data.name}
              emptyStringCompletionPolicy={EmptyStringCompletionPolicy.REPLACE_WITH_DEFAULT_ON_COMPLETE}
              defaultValue={getDefaultNameWhenEmpty}
              onDraftNameChanged={(name) => {
                setEditingNameError(
                  validateMethodName(name, allMethodNamesExcludingSelectedMethod, procedureType)
                );
              }}
              onCancel={() => {
                if (props.data.name.trim().length === 0) {
                  props.data.onChangeMethodName(methodCid, getDefaultNameWhenEmpty());
                }
              }}
              isError={Boolean(editingNameError)}
              maxValueLength={MAX_METHOD_NAME_LENGTH}
              placeholderText="Enter a name"
            />
          ) : (
            <MethodNameViewer methodCid={methodCid} name={props.data.name} isHoverable={false} />
          )}
          {!isEditingName && !isProcedureReadOnly && hasFullAccess && (
            <ProcedureMethodSettingsDropdown
              methodCid={methodCid}
              onDuplicateClick={props.data.onDuplicateClick}
              onEditMethodSelection={props.data.onEditMethod}
              onDeleteMethod={props.data.onDeleteMethod}
            />
          )}
          {isProcedureReadOnly && hasFullAccess && (
            <ProcedureMethodEditorViewButton
              methodCid={methodCid}
              onViewMethodEditor={props.data.onEditMethod}
            />
          )}
          <ProcedureNodeHandle
            type="source"
            id={createHandleIdForMethod(methodCid, 'source')}
            isVisible={false}
          />
          <ProcedureNodeHandle
            type="target"
            id={createHandleIdForMethod(methodCid, 'target')}
            isVisible={false}
          />
        </div>
        {editingNameError && (
          <div className={css(styles.errorWrapper)}>
            <FieldError message={editingNameError} />
          </div>
        )}
        <div className={methodBodyClass(hasValidationErrors)} {...hoverProps}>
          {shouldRenderMethodSection(FilteredProcedureMethodSection.MATERIALS) && (
            <ProcedureMaterialsMethodSection
              sectionTitle="Material"
              isHovered={isHovered}
              isLeafNode={isLeafNode}
              data={props.data}
              handlesInUse={handlesInUse}
              methodCid={methodCid}
              isProcedureReadOnly={isProcedureReadOnly}
              accessLevel={accessLevel}
            />
          )}
          {shouldRenderMethodSection(FilteredProcedureMethodSection.PARAMETERS) && (
            <ProcedureParametersMethodSection sectionTitle={parameterUIString} data={props.data} />
          )}
          {shouldRenderMethodSection(FilteredProcedureMethodSection.EQUIPMENT) && (
            <ProcedureEquipmentMethodSection sectionTitle={equipmentUIString} data={props.data} />
          )}
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  nameAndValidationBox: {
    display: 'flex',
    alignItems: 'center',
    ...multiLineEllipsisText(2),
  },
  viewMethodEditorButton: {
    float: 'right',
    zIndex: 100,
    padding: '8px',
    width: '14px',
    height: '14px',
    borderRadius: 4,
    background: GRAY_LIGHT,
    color: GRAY_DARKER,
    ':hover': {
      color: NEAR_BLACK,
      cursor: 'pointer',
    },
  },
  onHoverName: {
    backgroundColor: GRAY,
    cursor: 'text',
    paddingRight: '8px',
    marginRight: '8px',
  },
  errorWrapper: {
    padding: '0 9px 2px',
    borderLeft: `1px solid ${GRAY_DARK}`,
    borderRight: `1px solid ${GRAY_DARK}`,
    marginTop: '-2px',
  },
});
